import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { AlertTriangle, Info, CheckCircle, Trash2, X } from 'lucide-react';

const DialogContext = createContext(null);

export const useDialog = () => useContext(DialogContext);

const ICONS = {
  error:   { Icon: AlertTriangle, color: '#EF1D28',  bg: '#FFF1F2' },
  warning: { Icon: AlertTriangle, color: '#F97316',  bg: '#FFF7ED' },
  info:    { Icon: Info,          color: '#3B82F6',  bg: '#EFF6FF' },
  success: { Icon: CheckCircle,   color: '#10B981',  bg: '#ECFDF5' },
  danger:  { Icon: Trash2,        color: '#EF1D28',  bg: '#FFF1F2' },
};

const DialogOverlay = ({ dialog, onClose }) => {
  const isConfirm = dialog.kind === 'confirm';
  const iconKey = dialog.iconType || (isConfirm && dialog.danger !== false ? 'danger' : dialog.type || 'info');
  const { Icon, color, bg } = ICONS[iconKey] || ICONS.info;
  const confirmBg = dialog.danger !== false ? '#EF1D28' : 'linear-gradient(135deg,#FB923C,#F97316)';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(false); if (e.key === 'Enter') onClose(true); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={() => onClose(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 20, padding: '28px 28px 24px',
          width: '100%', maxWidth: 400, position: 'relative',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          animation: 'dialogIn 0.18s cubic-bezier(.34,1.56,.64,1)',
        }}
      >
        <style>{`@keyframes dialogIn{from{opacity:0;transform:scale(0.92) translateY(8px)}to{opacity:1;transform:none}}`}</style>

        {/* Close X */}
        <button
          onClick={() => onClose(false)}
          style={{
            position: 'absolute', top: 14, right: 14, border: 'none', background: 'transparent',
            cursor: 'pointer', color: '#9CA3AF', padding: 4, borderRadius: 6, lineHeight: 1,
          }}
        >
          <X size={18} />
        </button>

        {/* Icon badge */}
        <div style={{
          width: 52, height: 52, borderRadius: 16, background: bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18,
        }}>
          <Icon size={26} color={color} strokeWidth={2} />
        </div>

        {/* Title */}
        {dialog.title && (
          <div style={{ fontWeight: 800, fontSize: 17, color: '#111827', marginBottom: 8 }}>
            {dialog.title}
          </div>
        )}

        {/* Message */}
        <div style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.65, marginBottom: 26 }}>
          {dialog.message}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          {isConfirm && (
            <button
              onClick={() => onClose(false)}
              style={{
                border: '1.5px solid #E5E7EB', background: 'white', borderRadius: 10,
                padding: '10px 22px', fontSize: 14, fontWeight: 600,
                cursor: 'pointer', color: '#374151',
              }}
            >
              Cancel
            </button>
          )}
          <button
            autoFocus
            onClick={() => onClose(true)}
            style={{
              border: 'none', borderRadius: 10, padding: '10px 26px',
              fontSize: 14, fontWeight: 700, cursor: 'pointer', color: 'white',
              background: confirmBg,
            }}
          >
            {isConfirm ? (dialog.confirmLabel || 'Confirm') : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  const showAlert = useCallback((message, { title, type } = {}) =>
    new Promise(resolve => setDialog({ kind: 'alert', message, title, type: type || 'info', resolve }))
  , []);

  const showConfirm = useCallback((message, { title, confirmLabel, danger, iconType } = {}) =>
    new Promise(resolve => setDialog({ kind: 'confirm', message, title, confirmLabel, danger: danger !== false, iconType, resolve }))
  , []);

  const close = useCallback((result) => {
    setDialog(prev => { prev?.resolve(result); return null; });
  }, []);

  return (
    <DialogContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {dialog && <DialogOverlay dialog={dialog} onClose={close} />}
    </DialogContext.Provider>
  );
};
